// UI icon set. Inline SVG (no dep), uniform stroke style across the app.
// Reaction emojis (👍 ❤️ etc.) intentionally remain — those are content, not chrome.

import type { CSSProperties } from 'react';

export function IconRefresh({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

export function IconPalette({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="13.5" cy="6.5" r="1.5" />
      <circle cx="17.5" cy="10.5" r="1.5" />
      <circle cx="8.5" cy="7.5" r="1.5" />
      <circle cx="6.5" cy="12.5" r="1.5" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125 0-.937.75-1.688 1.688-1.688h1.984c3.094 0 5.625-2.531 5.625-5.625C22 6.985 17.524 2 12 2Z" />
    </svg>
  );
}

export function IconDownload({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

interface IconProps {
  size?: number;
  className?: string;
  strokeWidth?: number;
  style?: CSSProperties;
  filled?: boolean;
}

function svgProps(size: number, strokeWidth: number, className?: string, style?: CSSProperties) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    style,
    'aria-hidden': true,
  };
}

export function IconSettings({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

export function IconPaperclip({ size = 18, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.8l-8.58 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

export function IconMic({ size = 18, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  );
}

export function IconPhone({ size = 18, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

export function IconVideo({ size = 18, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="m22 8-6 4 6 4V8Z" />
      <rect x="2" y="6" width="14" height="12" rx="2" ry="2" />
    </svg>
  );
}

export function IconSmile({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" x2="9.01" y1="9" y2="9" />
      <line x1="15" x2="15.01" y1="9" y2="9" />
    </svg>
  );
}

export function IconPencil({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

export function IconTrash({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </svg>
  );
}

export function IconSearch({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function IconPlus({ size = 16, className, strokeWidth = 2, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

export function IconSend({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M22 2 11 13" />
      <path d="m22 2-7 20-4-9-9-4Z" />
    </svg>
  );
}

export function IconClose({ size = 16, className, strokeWidth = 2, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

export function IconChevronRight({ size = 14, className, strokeWidth = 2, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function IconLogout({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" x2="9" y1="12" y2="12" />
    </svg>
  );
}

export function IconHeart({ size = 16, className, strokeWidth = 1.75, style, filled }: IconProps) {
  return (
    <svg
      {...svgProps(size, strokeWidth, className, style)}
      fill={filled ? 'currentColor' : 'none'}
    >
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
    </svg>
  );
}

export function IconClock({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export function IconKey({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="7.5" cy="15.5" r="5.5" />
      <path d="M21 2 13 10" />
      <path d="m17 6 4 4" />
      <path d="m18 7 1.5 1.5" />
    </svg>
  );
}

export function IconLock({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export function IconBell({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

export function IconStorage({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <line x1="12" x2="12" y1="3" y2="15" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="5" x2="19" y1="21" y2="21" />
    </svg>
  );
}

export function IconHelp({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" x2="12.01" y1="17" y2="17" />
    </svg>
  );
}

export function IconUser({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function IconDevice({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" x2="16" y1="21" y2="21" />
      <line x1="12" x2="12" y1="17" y2="21" />
    </svg>
  );
}

export function IconArchive({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <rect width="20" height="5" x="2" y="3" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

export function IconStar({ size = 16, className, strokeWidth = 1.75, style, filled }: IconProps) {
  return (
    <svg
      {...svgProps(size, strokeWidth, className, style)}
      fill={filled ? 'currentColor' : 'none'}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export function IconChat({ size = 18, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function IconReply({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="9 17 4 12 9 7" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
  );
}

// Pin icon — used for "pin this message to the top of the chat".
// Stylized thumbtack with a tilt that reads as pinned.
export function IconPin({
  size = 14,
  className,
  strokeWidth = 1.75,
  style,
  filled,
}: IconProps) {
  return (
    <svg
      {...svgProps(size, strokeWidth, className, style)}
      fill={filled ? 'currentColor' : 'none'}
    >
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14l-2-5V6a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3v6Z" />
    </svg>
  );
}

// Mirror of IconReply pointing right — used for the Forward action on a
// message, the universal "send onward" affordance.
export function IconForward({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="15 17 20 12 15 7" />
      <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
    </svg>
  );
}

export function IconPhoneIncoming({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="16 2 16 8 22 8" />
      <line x1="22" x2="16" y1="2" y2="8" />
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

export function IconPhoneOutgoing({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="22 8 22 2 16 2" />
      <line x1="16" x2="22" y1="8" y2="2" />
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

export function IconPhoneMissed({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <line x1="22" x2="16" y1="2" y2="8" />
      <line x1="16" x2="22" y1="2" y2="8" />
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

// Double-check (✓✓) for the "read" state on read receipts. Distinct from
// IconCheck so the visual is unmistakable: two ticks, slightly closer
// together than two IconChecks side-by-side would be.
export function IconCheckDouble({
  size = 14,
  className,
  strokeWidth = 2,
  style,
}: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="2 12 7 17 14 6" />
      <polyline points="11 12 14 15 22 6" />
    </svg>
  );
}

export function IconCheck({ size = 14, className, strokeWidth = 2, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function IconAlert({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" x2="12" y1="9" y2="13" />
      <line x1="12" x2="12.01" y1="17" y2="17" />
    </svg>
  );
}

export function IconShield({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </svg>
  );
}

export function IconMaximize({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" x2="14" y1="3" y2="10" />
      <line x1="3" x2="10" y1="21" y2="14" />
    </svg>
  );
}

export function IconMinimize({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" x2="21" y1="10" y2="3" />
      <line x1="3" x2="10" y1="21" y2="14" />
    </svg>
  );
}

export function IconBellOff({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
      <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
      <path d="M18 8a6 6 0 0 0-9.33-5" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export function IconLink({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

export function IconImage({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

export function IconFile({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

export function IconUsers({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function IconArrowLeft({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

export function IconExit({ size = 16, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export function IconMoreVertical({ size = 14, className, strokeWidth = 1.75, style }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth, className, style)}>
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

// Logo / brand icon for the chat itself — speech bubble.
export function IconLogo({ size = 24, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <path d="M5 5h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-6l-5 4v-4a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3z" />
    </svg>
  );
}
